import { TOKEN_RECOVERY_EPISODE_TIMEOUT_MS } from '../tokenRecovery';
import { createDifferentAuthUserRefreshResult } from '../refreshResult';
import type { PendingRefreshRequest } from './RefreshRequests';
import type { TokenRefreshFailure, TokenRefreshRequest } from './types';

interface DelegationLease {
  requestedByUserId: string;
  requestedByTabId: string;
  requestId: string;
  requestStartedAt: number;
}

// A grant may arrive near the end of the requester's coordination window.
// Keep ownership through the recovery episode that starts only when it arrives.
const DELEGATION_LEASE_TIMEOUT_MS = 2 * TOKEN_RECOVERY_EPISODE_TIMEOUT_MS;

const createLease = (
  requestId?: string,
  requestStartedAt?: number,
  requestedByTabId?: string,
  requestedByUserId?: string | null
): DelegationLease | null => {
  if (
    !requestId ||
    !requestedByTabId ||
    !requestedByUserId ||
    typeof requestStartedAt !== 'number' ||
    !Number.isFinite(requestStartedAt)
  ) {
    return null;
  }

  return { requestedByUserId, requestedByTabId, requestId, requestStartedAt };
};

export class RefreshDelegation {
  private lease: DelegationLease | null = null;

  private timeout: number | null = null;

  public isActive(): boolean {
    return this.lease !== null;
  }

  public grant(
    data: TokenRefreshRequest,
    requestedByUserId: string,
    requestStartedAt = Date.now()
  ): TokenRefreshFailure | null {
    const lease = createLease(
      data.requestId,
      requestStartedAt,
      data.requestedByTabId,
      requestedByUserId
    );

    if (!lease) {
      return null;
    }

    this.setLease(lease);

    return {
      requestId: data.requestId,
      error: createDifferentAuthUserRefreshResult().error,
      terminalFailure: false,
      refreshLocally: true,
      delegateRefresh: true,
      delegatedToTabId: data.requestedByTabId,
      requestedByUserId,
      requestStartedAt
    };
  }

  public accept(
    data: TokenRefreshFailure,
    pendingRequest: PendingRefreshRequest | undefined,
    tabId: string
  ): PendingRefreshRequest | null {
    if (
      this.adoptLease(data) &&
      pendingRequest &&
      data.delegatedToTabId === tabId &&
      data.requestedByUserId === pendingRequest.requestedByUserId &&
      data.requestStartedAt === pendingRequest.requestStartedAt &&
      Date.now() - pendingRequest.requestStartedAt < TOKEN_RECOVERY_EPISODE_TIMEOUT_MS
    ) {
      return pendingRequest;
    }

    return null;
  }

  public complete(requestId: string | undefined, userId: string | null | undefined): void {
    if (userId && this.lease?.requestedByUserId === userId && requestId === this.lease.requestId) {
      this.clearLease();
    }
  }

  public destroy(): void {
    this.clearLease();
  }

  private adoptLease(data: TokenRefreshFailure): boolean {
    const lease = createLease(
      data.requestId,
      data.requestStartedAt,
      data.delegatedToTabId,
      data.requestedByUserId
    );

    if (!lease) {
      return false;
    }

    const activeLease = this.lease;

    if (activeLease) {
      return (
        activeLease.requestId === lease.requestId &&
        activeLease.requestStartedAt === lease.requestStartedAt &&
        activeLease.requestedByTabId === lease.requestedByTabId &&
        activeLease.requestedByUserId === lease.requestedByUserId
      );
    }

    this.setLease(lease);
    return true;
  }

  private setLease(lease: DelegationLease): void {
    this.clearLease();
    this.lease = lease;
    this.timeout = window.setTimeout(() => {
      if (this.lease === lease) {
        this.clearLease();
      }
    }, DELEGATION_LEASE_TIMEOUT_MS);
  }

  private clearLease(): void {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    this.lease = null;
  }
}
